import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Pii from 'dashboard/components/Pii';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import { formatGridAndNonGridAttributes } from 'dashboard/lib/case-layouts/utils';
import { isSafari } from 'dashboard/lib/utils/device';
import {
  SelfiePhotoName,
  VerificationCaptureMethod,
  VerificationSelfie,
} from 'dashboard/models/verification';
import { SelfieVerificationModuleAttribute } from 'dashboard/modules/ResourceVerification/model';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Banner from 'design-system/components/Banner';
import { paragraphMixins } from 'design-system/mixins/text';
import download from 'lib/download';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';

interface SelfieVerificationDetailProps extends VerificationDetailProps {
  layout?: SelfieVerificationModuleAttribute[];
  verification: VerificationSelfie;
}

const GRID_ATTRIBUTES = [
  SelfieVerificationModuleAttribute.CaptureMethod,
  SelfieVerificationModuleAttribute.CaptureMethodLeft,
  SelfieVerificationModuleAttribute.CaptureMethodRight,
  SelfieVerificationModuleAttribute.FromReusablePersona,
];

const DEFAULT_LAYOUT = [
  SelfieVerificationModuleAttribute.Photos,
  SelfieVerificationModuleAttribute.CaptureMethod,
  SelfieVerificationModuleAttribute.CaptureMethodLeft,
  SelfieVerificationModuleAttribute.CaptureMethodRight,
  SelfieVerificationModuleAttribute.FromReusablePersona,
  SelfieVerificationModuleAttribute.Checks,
];

const SelfieVerification = ({
  verification,
  layout = DEFAULT_LAYOUT,
}: SelfieVerificationDetailProps) => {
  const {
    // TODO: (lauren) will deprecate these in near future and only rely on 'photos' field
    'center-photo-url': centerPhotoUrl,
    'left-photo-url': leftPhotoUrl,
    'right-photo-url': rightPhotoUrl,
    video,
    'redacted-at': redactedAt,
    status,
    'from-persona': fromPersona,
    'from-reusable-persona': fromReusablePersona,
    'capture-method': captureMethod,
    'capture-method-left': captureMethodLeft,
    'capture-method-right': captureMethodRight,
  } = verification.attributes;

  const isRedacted = redactedAt != null;

  const showChecks =
    !!status &&
    ![
      VerificationStatus.Initiated,
      VerificationStatus.Confirmed,
      VerificationStatus.Submitted,
      VerificationStatus.Canceled,
    ].includes(status);
  const isVideoNotSupported = isSafari() && video?.['file-type'] === 'video/webm';

  const photos =
    verification.attributes.photos ??
    ([
      convertUrlToPhotoObj(centerPhotoUrl, 'center_photo'),
      convertUrlToPhotoObj(leftPhotoUrl, 'left_photo'),
      convertUrlToPhotoObj(rightPhotoUrl, 'right_photo'),
    ].filter(Boolean) as { url: string; name: SelfiePhotoName }[]);

  const getAttributeByType = (attr: SelfieVerificationModuleAttribute) => {
    switch (attr) {
      case SelfieVerificationModuleAttribute.CaptureMethod:
        return (
          <StyledAttributeGrid key='capture-method'>
            <Attribute
              label={
                fromPersona || fromReusablePersona
                  ? 'Original capture method (center)'
                  : 'Capture method (center)'
              }
              value={SelfieCaptureMethodDescription(captureMethod)}
            />
          </StyledAttributeGrid>
        );
      case SelfieVerificationModuleAttribute.CaptureMethodLeft:
        return (
          leftPhotoUrl != null && (
            <StyledAttributeGrid key='capture-method-left'>
              <Attribute
                label={
                  fromPersona || fromReusablePersona
                    ? 'Original capture method (left)'
                    : 'Capture method (left)'
                }
                value={SelfieCaptureMethodDescription(captureMethodLeft)}
              />
            </StyledAttributeGrid>
          )
        );
      case SelfieVerificationModuleAttribute.CaptureMethodRight:
        return (
          rightPhotoUrl != null && (
            <StyledAttributeGrid key='capture-method-right'>
              <Attribute
                label={
                  fromPersona || fromReusablePersona
                    ? 'Original capture method (right)'
                    : 'Capture method (right)'
                }
                value={SelfieCaptureMethodDescription(captureMethodRight)}
              />
            </StyledAttributeGrid>
          )
        );
      case SelfieVerificationModuleAttribute.Checks:
        return (
          showChecks &&
          verification.attributes.checks && <ChecksTable checks={verification.attributes.checks} />
        );
      case SelfieVerificationModuleAttribute.FromReusablePersona:
        return (
          <StyledAttributeGrid key='from-reusable-persona'>
            <Attribute
              label='Reusable Persona used'
              value={fromPersona || fromReusablePersona ? 'Yes' : 'No'}
            />
          </StyledAttributeGrid>
        );
      case SelfieVerificationModuleAttribute.Photos:
        return (
          <Pii key='photo-video' isRedacted={isRedacted} hideOnRedacted={true}>
            {video && !isVideoNotSupported && (
              <VideoWrapper>
                <video controls>
                  <source src={video.url} type={video['file-type']} />
                </video>
                <PhotoLabel>Video</PhotoLabel>
              </VideoWrapper>
            )}
            <ImageGrid>
              {photos.map((photo) => (
                <div key={`selfie-photo__${photo.name}`}>
                  <AsyncSelfie
                    value={[{ url: photo.url }]}
                    width='initial'
                    height='200px'
                    modalLabel={getPhotoLabel(photo.name)}
                    sourceToken={verification.id}
                  />
                  <PhotoLabel>{getPhotoLabel(photo.name)}</PhotoLabel>
                </div>
              ))}
            </ImageGrid>
          </Pii>
        );
    }
  };

  return (
    <InfoSection>
      {isVideoNotSupported && (
        <Banner variant='warning'>
          <VideoErrorInner>
            <CircleInfoIcon />
            <span>
              Video playback not available in Safari. &nbsp;
              <button
                id='selfie-verification__download-video'
                onClick={() => {
                  download({ url: video.url, filename: video['file-name'] });
                }}
              >
                Download video
              </button>{' '}
              or view in another browser.
            </span>
          </VideoErrorInner>
        </Banner>
      )}
      {formatGridAndNonGridAttributes(
        layout,
        GRID_ATTRIBUTES,
        getAttributeByType,
        StyledAttributeGrid,
      )}
    </InfoSection>
  );
};

export default SelfieVerification;

const SelfieCaptureMethodDescription = (captureMethod?: VerificationCaptureMethod) => {
  if (!captureMethod) {
    return null;
  }

  switch (captureMethod) {
    case VerificationCaptureMethod.Live:
      return 'Live';
    case VerificationCaptureMethod.Auto:
      return 'Live (autocapture)';
    case VerificationCaptureMethod.Manual:
      return 'Live (manual capture)';
    case VerificationCaptureMethod.Upload:
      // Capture method "upload" in selfie results from a stillfie, which uses the device's native
      // camera to take a selfie. Native camera usually allows taking a picture MANUALLY or picking
      // from a gallery of local photos, both of which are "uploaded" to our client. Some orgs have
      // been confused by this "upload" capture method so we rename in Dashboard to reduce confusion.
      return 'Manual';
    case VerificationCaptureMethod.Api:
      return 'API';
    default:
      return assertNever(captureMethod);
  }
};

const getPhotoLabel = (name: SelfiePhotoName) => {
  switch (name) {
    case 'center_photo':
    case 'photo':
      return 'Look Ahead';
    case 'left_photo':
      return 'Look Left';
    case 'right_photo':
      return 'Look Right';
    default:
      assertNever(name);
  }
};

const convertUrlToPhotoObj = (url: string | undefined, name: SelfiePhotoName) => {
  if (!url) {
    return null;
  }

  return {
    url,
    name,
  };
};

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: ${Spacing[4]};
  padding: 0 ${Spacing[3]};
`;

const PhotoLabel = styled.p`
  ${paragraphMixins.lgBold};
  margin: ${Spacing[2]} 0;
  text-align: center;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding: 0 ${Spacing[3]};
  max-width: 400px;
  & video {
    width: 100%;
    border-radius: ${({ theme }) => theme.element.borderRadius};
  }
`;

const VideoErrorInner = styled.span`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} 0;
  align-items: center;

  & button {
    background-color: transparent;
    color: ${({ theme }) => theme.color.text.brand};

    &:hover {
      cursor: pointer;
    }
  }
`;
